import React, { useEffect, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { injectIntl } from 'react-intl';
import { Switch, Route, Redirect } from 'react-router-dom';

import { authAdminSuperAdminPrivate } from './components/hoc/auth-admin-superadmin-private';
import { adminSuperAdminPrivate } from './components/hoc/admin-superadmin-private';
import { superAdminPrivate } from './components/hoc/superadmin-private';
import FooterMenu from './components/footer-menu';
import Popup from './components/common/popup';
import BoardInfo from './components/board-info';
import Progress from './components/common/progress';
import ArchivedBanner from './components/archived-banner';
import { getUserData } from './ducks/user';
import { isActiveToolbar } from './ducks/admin-panel';
import config from './config';
import m from './messages';

const AdminToolbar = React.lazy(() => import('./components/admin/toolbar'));
const AlertsPage = React.lazy(() => import('./pages/alerts'));
const AuthorPostsPage = React.lazy(() => import('./pages/author-posts'));
const CreateCategory = React.lazy(() => import('./components/admin/category/create'));
const DeleteCategory = React.lazy(() => import('./components/admin/category/delete'));
const MoveCategory = React.lazy(() => import('./components/admin/category/move'));
const RenameCategory = React.lazy(() => import('./components/admin/category/rename'));
const CreateBoard = React.lazy(() => import('./components/admin/board/create'));
const RenameBoard = React.lazy(() => import('./components/admin/board/rename'));
const MoveBoard = React.lazy(() => import('./components/admin/board/move'));
const MergeBoard = React.lazy(() => import('./components/admin/board/merge'));
const AdditionalBoard = React.lazy(() => import('./components/admin/board/additional'));
const DeleteBoard = React.lazy(() => import('./components/admin/board/delete'));
const ManageSpamPage = React.lazy(() => import('./components/routes/manage-spam'));
const ManageObjections = React.lazy(() => import('./components/admin/manage/objections'));
const ManageRights = React.lazy(() => import('./components/admin/manage/rights'));
const ManageBoards = React.lazy(() => import('./components/admin/manage/boards'));
const AdminList = React.lazy(() => import('./components/admin/admin-list'));
const NodeOptions = React.lazy(() => import('./components/admin/node/options'));
const RecycleBin = React.lazy(() => import('./components/admin/node/recycle-bin'));
const HomePage = React.lazy(() => import('./pages/home'));
const FavoritesPage = React.lazy(() => import('./pages/favorites'));
const CategoryPage = React.lazy(() => import('./pages/category'));
const ThreadPage = React.lazy(() => import('./pages/thread'));
const ThreadEditPage = React.lazy(() => import('./pages/thread-edit'));
const ThreadCreatePage = React.lazy(() => import('./pages/thread-create'));
const ThreadReplyPage = React.lazy(() => import('./pages/thread-reply'));
const BoardInfoEditPage = React.lazy(() => import('./pages/board-info-edit'));
const NotFoundPage = React.lazy(() => import('./pages/404'));
const MoveThread = React.lazy(() => import('./components/admin/thread/move'));
const PrintThreadPage = React.lazy(() => import('./components/thread/old_print'));
const ReportAbusePage = React.lazy(() => import('./pages/report-abuse'));
const VolunteerPage = React.lazy(() => import('./pages/volunteer'));
const SearchBoardsPage = React.lazy(() => import('./pages/search-boards'));
const SearchSurnamePage = React.lazy(() => import('./pages/search-surname'));
const AdvancedSearchPage = React.lazy(() =>
  import('./components/searches/advanced').then(mod => ({ default: mod.AdvancedSearchPage }))
);
const SearchResultsPage = React.lazy(() => import('./pages/search-results'));
const RequestBoardsPage = React.lazy(() => import('./pages/request-boards'));

const ProtectedCreateCategory = superAdminPrivate(CreateCategory);
const ProtectedDeleteCategory = superAdminPrivate(DeleteCategory);
const ProtectedMoveCategory = superAdminPrivate(MoveCategory);
const ProtectedRenameCategory = superAdminPrivate(RenameCategory);
const ProtectedCreateBoard = superAdminPrivate(CreateBoard);
const ProtectedBoardInfoEditPage = adminSuperAdminPrivate(BoardInfoEditPage);
const ProtectedRenameBoard = superAdminPrivate(RenameBoard);
const ProtectedMoveBoard = superAdminPrivate(MoveBoard);
const ProtectedMergeBoard = superAdminPrivate(MergeBoard);
const ProtectedAdditionalBoard = superAdminPrivate(AdditionalBoard);
const ProtectedDeleteBoard = superAdminPrivate(DeleteBoard);
const ProtectedManageSpamPage = superAdminPrivate(ManageSpamPage);
const ProtectedManageObjections = adminSuperAdminPrivate(ManageObjections);
const ProtectedManageRights = superAdminPrivate(ManageRights);
const ProtectedManageBoards = superAdminPrivate(ManageBoards);
const ProtectedAdminList = superAdminPrivate(AdminList);
const ProtectedNodeOptions = superAdminPrivate(NodeOptions);
const ProtectedRecycleBin = adminSuperAdminPrivate(RecycleBin);
const ProtectedMoveThread = adminSuperAdminPrivate(MoveThread);
const ProtectedRequestBoardsPage = authAdminSuperAdminPrivate(RequestBoardsPage);
const ProtectedAlertsPage = authAdminSuperAdminPrivate(AlertsPage);
const ProtectedFavoritesPage = authAdminSuperAdminPrivate(FavoritesPage);
const ProtectedVolunteerPage = authAdminSuperAdminPrivate(VolunteerPage);
const ProtectedThreadCreatePage = authAdminSuperAdminPrivate(ThreadCreatePage);
const ProtectedThreadEditPage = authAdminSuperAdminPrivate(ThreadEditPage);
const ProtectedThreadReplyPage = authAdminSuperAdminPrivate(ThreadReplyPage);
const ProtectedReportAbusePage = authAdminSuperAdminPrivate(ReportAbusePage);

export function Root({ intl }) {
  const dispatch = useDispatch();
  const activeToolbar = useSelector(isActiveToolbar);
  const { institution } = config;

  useEffect(() => {
    dispatch(getUserData());
  }, []);
  return (
    <div>
      <Helmet>
        <title>{intl.formatMessage(m.helmet)}</title>
        <meta name="data-screen-name" content="boards-ui : message-boards" />
      </Helmet>
      <ArchivedBanner />
      <BoardInfo />
      <Popup />
      {activeToolbar && (
        <Suspense fallback={null}>
          <div role='region' aria-label='Admin Bar'>
            <AdminToolbar />
          </div>
        </Suspense>
      )}
      <main>
        <Suspense fallback={<Progress />}>
          <Switch>
            <Route
              path={['/admin/category/create', '/admin/category/create/:path']}
              exact
              component={ProtectedCreateCategory}
            />
            <Route
              path='/admin/category/delete/:path'
              exact
              component={ProtectedDeleteCategory}
            />
            <Route
              path='/admin/category/move/:path'
              exact
              component={ProtectedMoveCategory}
            />
            <Route
              path='/admin/category/rename/:path'
              exact
              component={ProtectedRenameCategory}
            />
            <Route
              path='/admin/board/create/:path'
              exact
              component={ProtectedCreateBoard}
            />
            <Route
              path='/admin/board/info/edit/:path'
              exact
              component={ProtectedBoardInfoEditPage}
            />
            <Route
              path='/admin/board/rename/:path'
              exact
              component={ProtectedRenameBoard}
            />
            <Route
              path='/admin/board/move/:path'
              exact
              component={ProtectedMoveBoard}
            />
            <Route
              path='/admin/board/merge/:path'
              exact
              component={ProtectedMergeBoard}
            />
            <Route
              path='/admin/board/additional/:path'
              exact
              component={ProtectedAdditionalBoard}
            />
            <Route
              path='/admin/board/delete/:path'
              exact
              component={ProtectedDeleteBoard}
            />
            <Route
              path='/admin/manage/spam'
              exact
              component={ProtectedManageSpamPage}
            />
            <Route
              path='/admin/manage/:name(objections)'
              exact
              component={ProtectedManageObjections}
            />
            <Route
              path='/admin/manage/:name(rights)'
              exact
              component={ProtectedManageRights}
            />
            <Route
              path='/admin/manage/:name(boards)'
              exact
              component={ProtectedManageBoards}
            />
            <Route
              path={['/admin/list', '/admin/list/:path']}
              exact
              component={ProtectedAdminList}
            />
            <Route
              path={['/admin/options', '/admin/options/:path']}
              exact
              component={ProtectedNodeOptions}
            />
            <Route
              path='/admin/recycle-bin/:path'
              exact
              component={ProtectedRecycleBin}
            />
            <Route
              path='/:path/:id/move'
              exact
              component={ProtectedMoveThread}
            />
            <Route
              path={['/request-board', '/request-board/:path/:id?']}
              exact
              component={ProtectedRequestBoardsPage}
            />
            <Route path='/404' exact component={NotFoundPage} />
            <Route path='/' exact component={HomePage} />
            <Route
              path='/alerts'
              exact
              component={ProtectedAlertsPage}
            />
            <Route
              path='/favorites/:path'
              exact
              component={ProtectedFavoritesPage}
            />
            <Route
              path='/volunteer-admin/:path'
              exact
              component={ProtectedVolunteerPage}
            />
            <Route path='/search/results' exact component={SearchResultsPage} />
            <Route path='/search/advanced' exact component={AdvancedSearchPage} />
            <Route
              path='/search/boards/:query'
              exact
              component={SearchBoardsPage}
            />
            <Route path='/search/surname' exact component={SearchSurnamePage} />
            <Redirect exact from='/surnames' to='/search/surname' />
            <Redirect
              exact
              from='/topics.ancestry.membertree.membertrees'
              to='/topics.ancestry.membertrees'
            />
            {/*
            <!== Message Boards
            */}
            <Route
              path='/author-posts/:userId'
              exact
              component={AuthorPostsPage}
            />
            <Route path='/:path' exact component={CategoryPage} />
            <Route
              path='/:path/create'
              exact
              component={ProtectedThreadCreatePage}
            />
            <Route path='/:path/:threadId' exact component={ThreadPage} />
            <Route
              path='/:path/:threadId/edit'
              exact
              component={ProtectedThreadEditPage}
            />
            <Route
              path='/:path/:threadId/reply'
              exact
              component={ProtectedThreadReplyPage}
            />
            <Route path='/:path/:id/print' exact component={PrintThreadPage} />
            <Route
              path='/:path/:id/abuse'
              exact
              component={ProtectedReportAbusePage}
            />
            <Route path='*' exact component={NotFoundPage} />
          </Switch>
        </Suspense>
      </main>
      {institution ? null : (
        <footer>
          <FooterMenu />
        </footer>
      )}
    </div>
  );
}

export default injectIntl(Root);
